import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SortDirEnum } from '../../../../common-explain/models/sort';
import { SortDropdownItem } from '../../../../common-explain/components/ex-sort-dropdown/ex-sort-dropdown.component';
import { CommonExplainModule } from '../../../../common-explain/common-explain.module';
import { DbPostSearchSort } from '../../../models/tender-search-body';
import { ResponseDocumentSubtype } from '../../../models/response-document-subtype';
import {
  LIBRARY_SORT_DROPDOWN_ITEMS,
  LibrarySortField,
  MAX_REFERENCE_DOCUMENTS,
  ResponseProjectDocument,
} from '../../../models/response-project-document';
import { ApiResponseDocumentsService } from '../../../services/api/response-documents/api-response-documents.service';
import { DocumentIdentityComponent } from '../document-identity/document-identity.component';
import { DocumentImportZoneComponent, ImportHint } from '../document-import-zone/document-import-zone.component';
import { DocumentTypeFilterBarComponent } from '../document-type-filter-bar/document-type-filter-bar.component';
import { DuplicateResolverComponent } from '../duplicate-resolver/duplicate-resolver.component';
import { DuplicateItem, DuplicateResolution } from '../dedup/dedup.model';
import { getDocumentTypeColor, sortInternalDocumentSubtypes } from '../document-types';
import { RejectedFile } from '../../../models/rejected-file';

export type PickerTab = 'library' | 'import';

export interface PickerConfirmResult {
  attachIds: number[];
  detachDocumentIds: number[];
  importFiles: File[];
}

export interface AttachedLibraryDoc {
  projectDocId: number;
  subtypeId?: number;
}

interface StagedImport {
  name: string;
  file: File;
}

type StagedItem =
  | ({ kind: 'import' } & StagedImport)
  | {
      kind: 'library';
      name: string;
      libraryId: number;
      subtypeName?: string | null;
      usedCount?: number;
      ownerName?: string | null;
    };

@Component({
  selector: 'app-reference-document-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    CommonExplainModule,
    DocumentIdentityComponent,
    DocumentImportZoneComponent,
    DocumentTypeFilterBarComponent,
    DuplicateResolverComponent,
  ],
  templateUrl: './reference-document-picker.component.html',
  styleUrls: ['./reference-document-picker.component.scss'],
})
export class ReferenceDocumentPickerComponent implements OnInit, OnChanges, OnDestroy {
  readonly maxDocuments = MAX_REFERENCE_DOCUMENTS;

  @Input({ required: true }) subtypeOptions: ResponseDocumentSubtype[] = [];
  @Input() existingCount = 0;
  @Input() attachedByLibraryId: Record<number, AttachedLibraryDoc> = {};
  @Input() initialTab: PickerTab = 'library';

  readonly confirm = output<PickerConfirmResult>();
  readonly cancel = output<void>();

  tab: PickerTab = 'library';
  query = '';
  sort: LibrarySortField = 'name';
  activeSubtypeIds: number[] = [];
  documents: ResponseProjectDocument[] = [];
  totalCount = 0;
  loading = false;
  loadingMore = false;
  selectedIds = new Set<number>();
  staged: StagedItem[] = [];
  duplicates: DuplicateItem[] = [];
  rejected: RejectedFile[] = [];
  /** Stable inputs for the filter bar — never recreate on every CD cycle. */
  filterSubtypes: ResponseDocumentSubtype[] = [];
  subtypeCounts: Record<number, number> = {};
  selectedCounts: Record<number, number> = {};
  sortDefault: DbPostSearchSort = { field: 'name' as DbPostSearchSort['field'], dir: SortDirEnum.ASC };

  private duplicateCounter = 0;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private dedupTimers: ReturnType<typeof setTimeout>[] = [];
  private destroyed = false;
  private page = 0;
  private attachedIdsCache = new Set<number>();
  private subtypeOptionsKey = '';
  /** True after the first library fetch with content — keeps search chrome mounted. */
  private libraryHydrated = false;
  private subtypeByDocId = new Map<number, number>();

  readonly sortItems: SortDropdownItem[] = LIBRARY_SORT_DROPDOWN_ITEMS.map(item => ({
    label: item.label,
    value: item.value,
    direction: item.direction === 'asc' ? SortDirEnum.ASC : SortDirEnum.DESC,
    unidirectional: true,
  }));

  importHints: ImportHint[] = [];
  importFootnote = '';

  private readonly importHintDefs: { key: string; highlight?: boolean }[] = [
    { key: 'tenders.responses.documents.hint-internal-memoire', highlight: true },
    { key: 'tenders.responses.documents.hint-internal-reference' },
    { key: 'tenders.responses.documents.hint-internal-cv' },
    { key: 'tenders.responses.documents.hint-internal-fiche' },
    { key: 'tenders.responses.documents.hint-internal-presentation' },
    { key: 'tenders.responses.documents.hint-internal-justificatifs' },
  ];

  constructor(
    private apiDocuments: ApiResponseDocumentsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.tab = this.initialTab;
    this.applyAttachedSelection();
    this.importHints = this.importHintDefs.map(def => ({
      label: this.translate.instant(def.key),
      highlight: def.highlight,
    }));
    this.importFootnote = this.translate.instant('tenders.responses.documents.hint-max-size');
    this.syncFilterSubtypes();
    this.syncSortDefault();
    void this.fetchSubtypeCounts();
    void this.loadLibrary(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTab'] && !changes['initialTab'].firstChange) {
      this.tab = this.initialTab;
      this.cdr.markForCheck();
    }
    if (changes['subtypeOptions']) {
      this.syncFilterSubtypes();
      void this.fetchSubtypeCounts();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    for (const timer of this.dedupTimers) clearTimeout(timer);
    this.dedupTimers = [];
  }

  get hasMore(): boolean {
    return this.documents.length < this.totalCount;
  }

  get stagedImportCount(): number {
    return this.staged.filter(s => s.kind === 'import').length;
  }

  get addedCount(): number {
    let n = 0;
    for (const id of this.selectedIds) {
      if (!this.attachedIdsCache.has(id)) n++;
    }
    return n + this.stagedImportCount;
  }

  get removedCount(): number {
    let n = 0;
    for (const id of this.attachedIdsCache) {
      if (!this.selectedIds.has(id)) n++;
    }
    return n;
  }

  get changeCount(): number {
    return this.addedCount + this.removedCount;
  }

  /** Project docs that are not library-attached stay counted; library selection replaces those. */
  get totalPreview(): number {
    const nonLibraryExisting = this.existingCount - this.attachedIdsCache.size;
    return nonLibraryExisting + this.selectedIds.size + this.stagedImportCount;
  }

  get isOverQuota(): boolean {
    return this.totalPreview > this.maxDocuments;
  }

  get canConfirm(): boolean {
    return this.changeCount > 0 && !this.isOverQuota && !this.duplicates.some(d => !d.resolution);
  }

  get confirmLabelKey(): string {
    return this.removedCount > 0
      ? 'tenders.responses.documents.picker.update-project'
      : 'tenders.responses.documents.picker.add-to-project';
  }

  get hasImportItems(): boolean {
    // Keep the import pane mounted while resolved dupes are still animating out,
    // otherwise a sole “Utiliser la bibliothèque” click jumps straight to the empty dropzone.
    return this.staged.length > 0 || this.duplicates.length > 0 || this.rejected.length > 0;
  }

  /** True empty library only — never flash this after a search/filter that returned 0 hits. */
  get showLibraryEmpty(): boolean {
    return !this.libraryHydrated && !this.loading && this.totalCount === 0 && !this.query && this.activeSubtypeIds.length === 0;
  }

  /** Keep search/filter chrome mounted once the library has content or an active filter. */
  get showLibraryChrome(): boolean {
    return this.documents.length > 0 || this.totalCount > 0 || !!this.query || this.activeSubtypeIds.length > 0 || this.libraryHydrated;
  }

  subtypeOf(doc: ResponseProjectDocument): ResponseDocumentSubtype | undefined {
    return this.subtypeOptions.find(s => s.id === doc.responseDocumentSubtypeId);
  }

  subtypeName(doc: ResponseProjectDocument): string | null {
    const s = this.subtypeOf(doc);
    return !s || s.name === 'undefined' ? null : s.name;
  }

  subtypeLabel(doc: ResponseProjectDocument): string {
    const s = this.subtypeOf(doc);
    return s && s.name !== 'undefined' ? s.displayedName : '';
  }

  colorFor(doc: ResponseProjectDocument): string {
    return getDocumentTypeColor(this.subtypeName(doc));
  }

  isSelected(id: string | number): boolean {
    return this.selectedIds.has(Number(id));
  }

  toggle(doc: ResponseProjectDocument): void {
    const id = Number(doc.id);
    if (doc.responseDocumentSubtypeId != null) this.subtypeByDocId.set(id, doc.responseDocumentSubtypeId);
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
      this.staged = this.staged.filter(s => !(s.kind === 'library' && s.libraryId === id));
    } else next.add(id);
    this.selectedIds = next;
    this.syncSelectedCounts();
    this.cdr.markForCheck();
  }

  selectAllVisible(): void {
    const next = new Set(this.selectedIds);
    const allSelected = this.documents.length > 0 && this.documents.every(d => next.has(Number(d.id)));
    for (const d of this.documents) {
      const id = Number(d.id);
      if (d.responseDocumentSubtypeId != null) this.subtypeByDocId.set(id, d.responseDocumentSubtypeId);
      if (allSelected) {
        next.delete(id);
        this.staged = this.staged.filter(s => !(s.kind === 'library' && s.libraryId === id));
      } else next.add(id);
    }
    this.selectedIds = next;
    this.syncSelectedCounts();
    this.cdr.markForCheck();
  }

  get allVisibleSelected(): boolean {
    return this.documents.length > 0 && this.documents.every(d => this.selectedIds.has(Number(d.id)));
  }

  onSearch(text: string): void {
    const next = text ?? '';
    const clearing = !next && !!this.query;
    this.query = next;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    // Clearing should reload immediately so we don't sit on a stale empty result set.
    if (clearing) {
      void this.loadLibrary(true);
      return;
    }
    this.searchTimer = setTimeout(() => void this.loadLibrary(true), 250);
  }

  onSort(sort: DbPostSearchSort): void {
    const field = String(sort.field) as LibrarySortField;
    if (field === 'name' || field === 'used' || field === 'recent') {
      this.sort = field;
      this.syncSortDefault();
      void this.loadLibrary(true);
    }
  }

  onToggleSubtype(id: number): void {
    this.activeSubtypeIds = this.activeSubtypeIds.includes(id) ? [] : [id];
    void this.loadLibrary(true);
  }

  onClearSubtypes(): void {
    this.activeSubtypeIds = [];
    void this.loadLibrary(true);
  }

  loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) return;
    void this.loadLibrary(false);
  }

  onFilesSelected(files: File[]): void {
    void this.stageFiles(files);
  }

  onFilesRejected(rejected: RejectedFile[]): void {
    if (!rejected.length) return;
    this.rejected = [...this.rejected, ...rejected];
    this.cdr.markForCheck();
  }

  stagedTrackId(item: StagedItem): string {
    return item.kind === 'library' ? `lib-${item.libraryId}` : `file-${item.name}`;
  }

  removeStaged(item: StagedItem): void {
    if (item.kind === 'library') {
      const next = new Set(this.selectedIds);
      next.delete(item.libraryId);
      this.selectedIds = next;
      this.syncSelectedCounts();
    }
    this.staged = this.staged.filter(s => this.stagedTrackId(s) !== this.stagedTrackId(item));
    this.cdr.markForCheck();
  }

  async onDuplicateResolve(event: { item: DuplicateItem; resolution: DuplicateResolution }): Promise<void> {
    const { item, resolution } = event;
    if (item.resolution) return;
    item.resolution = resolution;
    item.phase = 'leaving';
    this.duplicates = [...this.duplicates];
    this.cdr.markForCheck();

    if (resolution === 'keep') {
      const libraryId = item.existingId;
      if (libraryId == null || !Number.isFinite(libraryId)) {
        this.scheduleDedupDismiss(item);
        return;
      }

      if (item.existingSubtypeId != null) this.subtypeByDocId.set(libraryId, item.existingSubtypeId);

      // Already on the project: keep the dedup resolved trail only — no staged row / no count bump.
      if (this.attachedIdsCache.has(libraryId)) {
        this.scheduleDedupDismiss(item);
        return;
      }

      const next = new Set(this.selectedIds);
      next.add(libraryId);
      this.selectedIds = next;
      this.syncSelectedCounts();

      if (!this.staged.some(s => s.kind === 'library' && s.libraryId === libraryId)) {
        const subtype = this.subtypeOptions.find(s => s.id === item.existingSubtypeId);
        this.staged = [
          ...this.staged,
          {
            kind: 'library',
            name: item.name,
            libraryId,
            subtypeName: subtype && subtype.name !== 'undefined' ? subtype.name : null,
            usedCount: item.usedCount,
            ownerName: item.ownerName,
          },
        ];
      }

      this.scheduleDedupDismiss(item);
      return;
    }

    if (resolution === 'replace' && item.file) {
      // Continue import: stage the file without touching the library.
      if (!this.staged.some(s => s.kind === 'import' && s.name === item.name)) {
        this.staged = [...this.staged, { kind: 'import', name: item.name, file: item.file }];
      }
    }

    this.scheduleDedupDismiss(item);
  }

  private scheduleDedupDismiss(item: DuplicateItem): void {
    this.dedupTimers.push(
      setTimeout(() => {
        item.phase = 'collapsing';
        this.duplicates = [...this.duplicates];
        this.cdr.markForCheck();
      }, 1500),
      setTimeout(() => {
        this.duplicates = this.duplicates.filter(d => d.uid !== item.uid);
        this.cdr.markForCheck();
      }, 1800)
    );
  }

  onConfirm(): void {
    if (!this.canConfirm) return;

    const attachIds: number[] = [];
    for (const id of this.selectedIds) {
      if (!this.attachedIdsCache.has(id)) attachIds.push(id);
    }

    const detachDocumentIds: number[] = [];
    for (const [libId, ref] of Object.entries(this.attachedByLibraryId)) {
      if (!this.selectedIds.has(Number(libId))) detachDocumentIds.push(ref.projectDocId);
    }

    this.confirm.emit({
      attachIds,
      detachDocumentIds,
      importFiles: this.staged.filter((s): s is StagedItem & { kind: 'import' } => s.kind === 'import').map(s => s.file),
    });
  }

  goToLibrary(): void {
    void this.router.navigate(['/tenders/library']);
  }

  private syncFilterSubtypes(): void {
    const next = sortInternalDocumentSubtypes(this.subtypeOptions.filter(s => s.name !== 'undefined'));
    const key = next.map(s => s.id).join(',');
    if (key === this.subtypeOptionsKey) return;
    this.subtypeOptionsKey = key;
    this.filterSubtypes = next;
  }

  private syncSortDefault(): void {
    this.sortDefault = {
      field: this.sort as DbPostSearchSort['field'],
      dir: this.sort === 'name' ? SortDirEnum.ASC : SortDirEnum.DESC,
    };
  }

  private async fetchSubtypeCounts(): Promise<void> {
    const subtypes = this.filterSubtypes;
    if (!subtypes.length) {
      this.subtypeCounts = {};
      this.cdr.markForCheck();
      return;
    }
    try {
      const result = await firstValueFrom(this.apiDocuments.countLibraryBySubtype(subtypes.map(s => s.id)));
      if (this.destroyed) return;
      this.subtypeCounts = result;
      this.syncSelectedCounts();
      this.cdr.markForCheck();
    } catch {
      // Keep previous counts on failure.
    }
  }

  private applyAttachedSelection(): void {
    this.attachedIdsCache = new Set(Object.keys(this.attachedByLibraryId).map(Number));
    this.selectedIds = new Set(this.attachedIdsCache);
    for (const [id, ref] of Object.entries(this.attachedByLibraryId)) {
      if (ref.subtypeId != null) this.subtypeByDocId.set(Number(id), ref.subtypeId);
    }
    this.syncSelectedCounts();
  }

  private syncSelectedCounts(): void {
    const counts: Record<number, number> = {};
    for (const id of this.selectedIds) {
      const subtypeId = this.subtypeByDocId.get(id);
      if (subtypeId == null) continue;
      counts[subtypeId] = (counts[subtypeId] ?? 0) + 1;
    }
    this.selectedCounts = counts;
  }

  private rememberDocSubtypes(docs: ResponseProjectDocument[]): void {
    for (const doc of docs) {
      if (doc.responseDocumentSubtypeId != null) {
        this.subtypeByDocId.set(Number(doc.id), doc.responseDocumentSubtypeId);
      }
    }
  }

  private async loadLibrary(reset: boolean): Promise<void> {
    if (reset) {
      this.loading = true;
      // Keep previous rows while refreshing so the filter chrome (and search focus) stays mounted.
    } else {
      this.loadingMore = true;
    }
    this.cdr.markForCheck();

    const page = reset ? 0 : this.page + 1;
    try {
      const res = await firstValueFrom(this.apiDocuments.searchLibraryPage(this.query, this.activeSubtypeIds, page, this.sort));
      if (this.destroyed) return;
      this.documents = reset ? res.data : [...this.documents, ...res.data];
      this.page = page;
      this.totalCount = res.totalCount ?? 0;
      if (this.totalCount > 0 || this.documents.length > 0) this.libraryHydrated = true;
      this.rememberDocSubtypes(res.data);
      this.syncSelectedCounts();
    } catch {
      if (this.destroyed) return;
      if (reset) {
        this.documents = [];
        this.totalCount = 0;
      }
    } finally {
      if (!this.destroyed) {
        this.loading = false;
        this.loadingMore = false;
        this.cdr.markForCheck();
      }
    }
  }

  private async stageFiles(files: File[]): Promise<void> {
    const have = new Set([...this.staged.map(s => s.name), ...this.duplicates.map(d => d.name)]);
    const fresh = files.filter(f => !have.has(f.name));
    if (!fresh.length) return;

    const toStage: StagedItem[] = [];
    const toDupe: DuplicateItem[] = [];

    for (const file of fresh) {
      try {
        const res = await firstValueFrom(this.apiDocuments.findByNameInLibrary(file.name));
        const match = res.data.find(d => d.name === file.name);
        if (match) {
          toDupe.push({
            uid: `dup-${++this.duplicateCounter}`,
            name: file.name,
            existingId: Number(match.id),
            existingSubtypeId: match.responseDocumentSubtypeId,
            file,
            usedCount: match.projects?.length ?? 0,
            ownerName: match.uploader?.name ?? null,
          });
        } else {
          toStage.push({ kind: 'import', name: file.name, file });
        }
      } catch {
        toStage.push({ kind: 'import', name: file.name, file });
      }
    }

    this.staged = [...this.staged, ...toStage];
    this.duplicates = [...this.duplicates, ...toDupe];
    this.cdr.markForCheck();
  }
}
